import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Crown, Check, Star, Lock, Loader2, ShieldCheck, Wand2 } from "lucide-react";
import { useAuth } from "../App";
import { useLanguage } from "../contexts/LanguageContext";
import { usePurchase } from "../hooks/usePurchase";
import { PACKS, PLANS, PLAN_FEATURES, PLAY_STORE_URL, isInstalledApp } from "../lib/products";

const EVENT = "lc:paywall";

export const openPaywall = (reason = "quota") =>
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { reason } }));

// Toute réponse 402 (quota / crédits insuffisants) ouvre automatiquement le paywall
if (typeof window !== "undefined" && !window.__lcFetchPatched) {
  const original = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = String(input?.url || input || "");
    // Langue de l'app transmise au serveur (profils IA localisés FR / UK / USA)
    if (url.includes("/api/")) {
      const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
      if (!headers.has("X-App-Lang")) headers.set("X-App-Lang", localStorage.getItem("language") || "fr");
      const navCountry = (navigator.language || "").split("-")[1];
      if (navCountry && !headers.has("X-App-Country")) headers.set("X-App-Country", navCountry.toUpperCase());
      init = { ...init, headers };
    }
    const res = await original(input, init);
    if (res.status === 402) {
      const reason = url.includes("/ai/parse-search") ? "ai_search" : url.includes("/conversations/unlock") ? "unlock"
        : url.includes("/messages") ? "messages" : url.includes("/likes") ? "likes" : "quota";
      openPaywall(reason);
    }
    return res;
  };
  window.__lcFetchPatched = true;
}

const REASONS = {
  messages: { fr: "Vos 3 messages gratuits du jour sont épuisés", en: "Your 3 free daily messages are used up" },
  unlock: { fr: "Débloquez ses messages et lisez la suite", en: "Unlock her messages and read more" },
  likes: { fr: "Découvrez qui craque pour vous", en: "Discover who likes you" },
  ai_search: { fr: "Débloquez la Recherche IA : découvrez des profils exclusifs, non visibles dans la découverte classique", en: "Unlock AI Search: discover exclusive profiles not visible in regular discovery" },
  promo: { fr: "Offre spéciale : boostez vos rencontres", en: "Special offer: boost your dating" },
  quota: { fr: "Vous n'avez plus assez de crédits", en: "You don't have enough credits" },
};

function PlanCard({ plan, lang, busy, onBuy, showPrice }) {
  return (
    <button
      onClick={() => onBuy(plan.sku, true)}
      disabled={!!busy}
      data-testid={`paywall-plan-${plan.id}`}
      className={`relative flex-1 text-left rounded-2xl p-4 border transition-[transform,border-color] duration-200 hover:-translate-y-0.5 active:scale-[0.98] ${
        plan.popular ? "border-yellow-400/70 bg-gradient-to-b from-yellow-500/15 to-transparent" : "border-white/10 bg-white/[0.03] hover:border-white/25"}`}
    >
      {plan.popular && (
        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap px-2.5 py-0.5 rounded-full bg-yellow-400 text-black text-[10px] font-bold tracking-wide">
          {lang === "fr" ? "MEILLEURE OFFRE" : "BEST VALUE"}
        </span>
      )}
      <p className="text-xs uppercase tracking-widest text-white/50">{plan.name[lang]}</p>
      {showPrice
        ? <p className="mt-1 text-2xl font-bold text-white" data-testid={`paywall-plan-price-${plan.id}`}>{plan.price}<span className="text-xs font-normal text-white/50">{plan.period[lang]}</span></p>
        : <p className="mt-1 text-2xl font-bold text-white">{plan.duration[lang]}</p>}
      <p className="mt-1 text-xs font-semibold text-yellow-400">+{plan.credits} {lang === "fr" ? "crédits" : "credits"} / {plan.duration[lang]}</p>
      <div className="mt-3 h-9 rounded-xl flex items-center justify-center text-sm font-bold bg-gradient-to-r from-yellow-400 to-orange-500 text-black">
        {busy === plan.sku ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "fr" ? "S'abonner" : "Subscribe")}
      </div>
    </button>
  );
}

function PackTile({ pack, lang, busy, onBuy, showPrice }) {
  return (
    <button
      onClick={() => onBuy(pack.sku, false)}
      disabled={!!busy}
      data-testid={`paywall-pack-${pack.id}`}
      className={`relative rounded-2xl p-3 border flex flex-col items-center transition-[transform,border-color] duration-200 hover:-translate-y-0.5 active:scale-[0.97] ${
        pack.popular ? "border-primary/70 bg-primary/10" : "border-white/10 bg-white/[0.03] hover:border-white/25"}`}
    >
      {pack.bonus && <span className="absolute -top-2 right-2 px-1.5 py-0.5 rounded-full bg-primary text-white text-[9px] font-bold">{pack.bonus}</span>}
      <img src={pack.image} alt="" className="w-12 h-12 object-contain" />
      <p className="mt-1 text-white font-bold text-sm flex items-center gap-1"><Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />{pack.credits}</p>
      <p className="text-[11px] text-white/60">{busy === pack.sku ? <Loader2 className="w-3 h-3 animate-spin" /> : (showPrice ? pack.price : (lang === "fr" ? "crédits" : "credits"))}</p>
    </button>
  );
}

export default function PaywallHost() {
  const [reason, setReason] = useState(null);
  const [webNotice, setWebNotice] = useState(false);
  const auth = useAuth();
  const { language } = useLanguage();
  const lang = language === "en" ? "en" : "fr";
  const { buy, busy, isGooglePlay } = usePurchase();
  // Dans l'app Play Store, Google affiche le prix local dans sa fenêtre de paiement : aucun prix ici
  const showPrice = !(isGooglePlay || isInstalledApp());

  useEffect(() => {
    const h = (e) => { setWebNotice(false); setReason(e.detail?.reason || "quota"); };
    window.addEventListener(EVENT, h);
    return () => window.removeEventListener(EVENT, h);
  }, []);

  const onBuy = async (sku, isSub) => {
    const res = await buy(sku, isSub, { inline: true });
    if (res === "web_unavailable") setWebNotice(true);
    else if (res) setReason(null);
  };

  return (
    <AnimatePresence>
      {reason && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center"
          onClick={() => setReason(null)}
          data-testid="paywall-overlay"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-md max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-white/10 bg-[#0d0a0f] px-5 pt-6"
            style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
            data-testid="paywall-modal"
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(ellipse_at_top,rgba(225,29,72,0.35),transparent_70%)]" />
            <button onClick={() => setReason(null)} className="absolute z-10 top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/70 hover:text-white" data-testid="paywall-close">
              <X className="w-4 h-4" />
            </button>

            <div className="relative text-center">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-[0_0_30px_rgba(250,204,21,0.35)]">
                {reason === "unlock" ? <Lock className="w-7 h-7 text-black" /> : reason === "ai_search" ? <Wand2 className="w-7 h-7 text-black" /> : <Crown className="w-7 h-7 text-black" />}
              </div>
              <h2 className="mt-3 font-heading text-2xl font-bold text-white">{lang === "fr" ? "Passez Premium" : "Go Premium"}</h2>
              <p className="mt-1 text-sm text-white/60" data-testid="paywall-reason">{(REASONS[reason] || REASONS.quota)[lang]}</p>
            </div>

            <ul className="relative mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1">
              {PLAN_FEATURES.map((f) => (
                <li key={f.fr} className="flex items-center gap-1 text-xs text-white/80"><Check className="w-3.5 h-3.5 text-green-400" />{f[lang]}</li>
              ))}
            </ul>

            <div className="relative mt-5 flex gap-3">
              {PLANS.map((p) => <PlanCard key={p.id} plan={p} lang={lang} busy={busy} onBuy={onBuy} showPrice={showPrice} />)}
            </div>

            <div className="mt-5 flex items-center gap-3 text-[11px] uppercase tracking-widest text-white/40">
              <span className="h-px flex-1 bg-white/10" />{lang === "fr" ? "ou rechargez vos crédits" : "or top up credits"}<span className="h-px flex-1 bg-white/10" />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {PACKS.map((p) => <PackTile key={p.id} pack={p} lang={lang} busy={busy} onBuy={onBuy} showPrice={showPrice} />)}
            </div>

            {webNotice && (
              <div className="mt-4 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 text-center text-xs text-yellow-200" data-testid="paywall-web-notice">
                {lang === "fr" ? "Paiement web bientôt disponible. Achetez dès maintenant depuis l'application :" : "Web payment coming soon. Buy now from the app:"}{" "}
                <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline" data-testid="paywall-playstore-link">Google Play</a>
              </div>
            )}

            <p className="mt-4 text-center text-[11px] text-white/40 flex items-center justify-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5" />
              {lang === "fr" ? "Paiement sécurisé • Résiliable à tout moment" : "Secure payment • Cancel anytime"} • {auth?.user?.credits ?? 0} {lang === "fr" ? "crédits" : "credits"}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
